import React, { useState, useEffect } from 'react';
import { skillsAPI, customersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const Skills = () => {
  const { isAdmin } = useAuth();
  const [skills, setSkills] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    customer_id: '',
    is_active: true,
    dnis: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [skillsData, customersData] = await Promise.all([
        skillsAPI.getSkills(),
        customersAPI.getCustomers(),
      ]);
      setSkills(skillsData);
      setCustomers(customersData);
    } catch (error) {
      console.error('Failed to load data:', error);
      alert('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingSkill(null);
    setFormData({
      name: '',
      description: '',
      customer_id: '',
      is_active: true,
      dnis: '',
    });
    setShowModal(true);
  };

  const handleEdit = (skill) => {
    setEditingSkill(skill);
    setFormData({
      name: skill.name,
      description: skill.description || '',
      customer_id: skill.customer_id,
      is_active: skill.is_active,
      dnis: skill.dnis || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (skillId) => {
    if (!window.confirm('Are you sure you want to delete this skill?')) return;
    try {
      await skillsAPI.deleteSkill(skillId);
      alert('Skill deleted successfully');
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete skill');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSkill) {
        await skillsAPI.updateSkill(editingSkill._id, formData);
        alert('Skill updated successfully');
      } else {
        await skillsAPI.createSkill(formData);
        alert('Skill created successfully');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save skill');
    }
  };

  const getCustomerName = (customerId) => {
    const c = customers.find(c => c._id === customerId);
    return c ? c.name : 'Unknown';
  };

  if (loading) {
    return (
      <div style={styles.container}><p>Loading...</p></div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.pageTitle}>Skill Management</h1>
        <button style={styles.createBtn} onClick={handleCreate}>Create Skill</button>
      </div>

      <div style={styles.tableSection}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeader}>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Customer</th>
              <th style={styles.th}>Description</th>
              {isAdmin && <th style={styles.th}>CALLERID</th>}
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {skills.map((skill) => (
              <tr key={skill._id} style={styles.tableRow}>
                <td style={styles.td}>{skill.name}</td>
                <td style={styles.td}>{getCustomerName(skill.customer_id)}</td>
                <td style={styles.td}>{skill.description || 'N/A'}</td>
                {isAdmin && <td style={styles.td}>{skill.dnis || 'N/A'}</td>}
                <td style={styles.td}>
                  <span style={{ ...styles.badge, backgroundColor: skill.is_active ? '#27ae60' : '#e74c3c' }}>
                    {skill.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={styles.td}>
                  <button style={styles.actionBtn} onClick={() => handleEdit(skill)}>Edit</button>
                  <button style={{ ...styles.actionBtn, ...styles.deleteBtn }} onClick={() => handleDelete(skill._id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2>{editingSkill ? 'Edit Skill' : 'Create Skill'}</h2>
            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Name</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} style={styles.input} required />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Customer</label>
                <select value={formData.customer_id} onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })} style={styles.input} required>
                  <option value="">Select Customer</option>
                  {customers.map((c) => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} style={{ ...styles.input, minHeight: '80px' }} />
              </div>
              {isAdmin && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>CALLERID</label>
                  <input type="text" value={formData.dnis} onChange={(e) => setFormData({ ...formData, dnis: e.target.value })} style={styles.input} placeholder="e.g. 1001" />
                </div>
              )}
              <div style={styles.formGroup}>
                <label style={styles.checkboxLabel}>
                  <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} style={styles.checkbox} />
                  Active
                </label>
              </div>
              <div style={styles.modalActions}>
                <button type="submit" style={styles.submitBtn}>{editingSkill ? 'Update' : 'Create'}</button>
                <button type="button" style={styles.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { maxWidth: '1400px', margin: '0 auto', padding: '2rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' },
  pageTitle: { color: '#60a5fa', margin: 0, fontWeight: '700', textShadow: '0 0 10px rgba(96, 165, 250, 0.3)' },
  createBtn: { padding: '0.75rem 1.5rem', backgroundColor: '#60a5fa', backgroundImage: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', cursor: 'pointer', boxShadow: '0 2px 8px rgba(96, 165, 250, 0.3)' },
  tableSection: { backgroundColor: '#1f2937', padding: '1.5rem', borderRadius: '12px', border: '1px solid #374151', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '600px' },
  tableHeader: { backgroundColor: '#111827', backgroundImage: 'linear-gradient(135deg, #111827 0%, #1f2937 100%)', borderBottom: '2px solid #60a5fa' },
  th: { padding: '1rem', textAlign: 'left', fontWeight: '600', letterSpacing: '0.3px', color: '#e2e8f0' },
  tableRow: { borderBottom: '1px solid #374151' },
  td: { padding: '1rem', color: '#9ca3af' },
  badge: { padding: '0.25rem 0.75rem', borderRadius: '12px', color: 'white', fontSize: '12px', fontWeight: '600' },
  actionBtn: { padding: '0.5rem 1rem', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '0.5rem', fontSize: '14px' },
  deleteBtn: { backgroundColor: '#e74c3c' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '1rem' },
  modalContent: { backgroundColor: '#1f2937', padding: '1.5rem', borderRadius: '12px', width: '90%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto', border: '1px solid #374151', color: '#e2e8f0' },
  form: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  formGroup: { display: 'flex', flexDirection: 'column' },
  label: { marginBottom: '0.5rem', color: '#9ca3af', fontWeight: '500', fontSize: '14px' },
  input: { padding: '0.75rem', border: '1px solid #374151', borderRadius: '8px', fontSize: '16px', backgroundColor: '#111827', color: '#e2e8f0', outline: 'none' },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#e2e8f0', fontWeight: '500' },
  checkbox: { width: '20px', height: '20px' },
  modalActions: { display: 'flex', gap: '1rem', marginTop: '1rem' },
  submitBtn: { flex: 1, padding: '0.75rem', backgroundColor: '#27ae60', backgroundImage: 'linear-gradient(135deg, #27ae60 0%, #219a52 100%)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', cursor: 'pointer' },
  cancelBtn: { flex: 1, padding: '0.75rem', backgroundColor: '#374151', color: '#e2e8f0', border: '1px solid #4b5563', borderRadius: '8px', fontSize: '16px', cursor: 'pointer' },
};

export default Skills;
